import { useEffect, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import InputText from '@/design_system/InputText';
import { useUpdateDefect } from '@/models/article';
import { ArticleDefectWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useViewPort from '@/utils/useViewport';

export const DefectCustomDescriptionInput = ({
  defect,
}: {
  defect: ArticleDefectWithRelations;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { request, article, errors } = useArticleContext();

  const { mutateAsync: updateDefect } = useUpdateDefect({
    requestId: request.id,
    articleId: article.id,
  });

  const debouncedUpdateDefect = useMemo(() => {
    return debounce(updateDefect, 500);
  }, [updateDefect]);

  const [customDescription, setCustomDescription] = useState(defect.customDescription ?? '');

  useEffect(() => {
    setCustomDescription(defect.customDescription ?? '');
    debouncedUpdateDefect.cancel();
  }, [defect.customDescription, debouncedUpdateDefect]);

  const isMissingDescription = !!errors.defects?.defects?.find((def) => defect?.id === def.id)
    ?.missingDescription;

  const label = t({
    id: 'defect-card.description.custom-defect',
    message: 'Description of custom defect',
  });

  return (
    <InputText
      label={isMobile ? label : undefined}
      aria-label={isMobile ? undefined : label}
      placeholder={t({
        id: 'defect-card.description.custom-defect.placeholder',
        message: 'Add a description',
      })}
      onChange={(value) => {
        setCustomDescription(value);
        debouncedUpdateDefect({
          defectId: defect.id,
          body: {
            description: value.trim(),
          },
        });
      }}
      value={customDescription}
      isInvalid={isMissingDescription}
    />
  );
};
